import type {ChipExperiment} from '../types/experiment';
import type {DefaultChIPSampleGroup} from './chip';

interface CreateChipAnalysisRequestBody {
    analysisMetadata: {
        name: string;
        description: string;
        organism: 'Homo sapiens' | 'Mus musculus';
        experiment_id: string;
        analysis_type: string;
        group_options?: Record<string, string[]>;
    };
    sample_ids: string[];
}

/**
 * Contact bff to _create_ a ChIP analysis. Note that starting the analysis is
 * a different request that should follow afterwards.
 *
 * TODO: There is a lot of overlap with the createAnalysis for RNA experiments,
 * consolidate the functions
 *
 * @return the created analysis
 * @throws error in case bff request was not ok
 */
export const createAnalysis = async (
    experiment: ChipExperiment,
    formData: ChipFormData | FEFormData | DBFormData | MotifFormData,
    apiHost: string,
    accessToken: string,
    samples?: {id: string}[]
): Promise<any> => {
    const endpoint = `${apiHost}/experiment/${experiment.id}/analysis/create`;
    const method = 'POST';

    const metadata = {
        name: formData.name,
        description: formData.description || '',
        organism: experiment.organism,
        experiment_id: experiment.id,
        analysis_type: formData.type
    } as CreateChipAnalysisRequestBody['analysisMetadata'];

    // differential binding analysis requires group Options
    if (formData.type === 'Differential Binding') {
        metadata.group_options = formData.selected_group_options;
    }

    // For default chip analysis, all samples are passed as parameter. For DBA, FE and motif, we pass selected samples from formData.samples itself
    // If samples is not passed when needed for createAnalysis it will assign sample_ids = [], which will return an error from bff
    const sample_ids = formData.type === 'ChIP Seq Default' ? (samples?.map(s => s.id) ?? []) : formData.sample_ids;

    const body: CreateChipAnalysisRequestBody = {
        analysisMetadata: metadata,
        sample_ids
    };

    const response = await fetch(endpoint, {
        method: method,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    });
    const responseData = await response.json();
    if (!response.ok) {
        throw new Error(`Error creating analysis: ${responseData.error || 'Unknown error'}`);
    }
    return responseData;
};

/**
 * Start an analysis by contacting BFF
 */
export const startAnalysis = async (
    experiment_id: string,
    analysis_id: string,
    formData: ChipFormData | FEFormData | DBFormData | MotifFormData,
    apiHost: string,
    accessToken: string
): Promise<any> => {
    const endpoint = `${apiHost}/experiment/${experiment_id}/analysis/${analysis_id}/start`;
    const method = 'POST';
    let body;
    if (formData.type === 'ChIP Seq Default') {
        body = {
            qvalue: formData.default_chip_analysis.peak_calling_qvalue,
            calling_type: formData.default_chip_analysis.peak_calling_type,
            tss_region_upstream: formData.default_chip_analysis.peak_annotation_tss_region_upstream,
            tss_region_downstream: formData.default_chip_analysis.peak_annotation_tss_region_downstream,
            level: formData.default_chip_analysis.peak_annotation_level,
            overlap: formData.default_chip_analysis.peak_annotation_overlap,
            genomic_annotation_priority: formData.default_chip_analysis.peak_annotation_genomic_annotation_priority
        };
    } else if (formData.type === 'Functional Enrichment') {
        body = {
            ontology: formData.functional_enrichment_analysis.ontology,
            p_adjust_method: formData.functional_enrichment_analysis.p_adjust_method,
            q_value_cutoff: formData.functional_enrichment_analysis.q_value_cutoff,
            default_ChIP_analysis: formData.default_ChIP_analysis
        };
    } else if (formData.type === 'Differential Binding') {
        body = {
            significance_threshold: formData.differential_binding_analysis.significance_threshold,
            summits: formData.differential_binding_analysis.summits,
            tss_region: formData.differential_binding_analysis.tss_region,
            level: formData.differential_binding_analysis.level,
            overlap: formData.differential_binding_analysis.overlap,
            genomic_annotation_priority: formData.differential_binding_analysis.genomic_annotation_priority,
            selected_group_options: formData.selected_group_options,
            default_ChIP_analysis: formData.default_ChIP_analysis
        };
    } else if (formData.type === 'ChIP Motif') {
        body = {
            ame_method: formData.motif_analysis.ame_method,
            nmotifs: formData.motif_analysis.nmotifs,
            e_val: formData.motif_analysis.e_val,
            differential_binding_analysis_id: formData.differential_binding_analysis_id
        };
    }

    const response = await fetch(endpoint, {
        method: method,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    });
    const responseData = await response.json();
    if (!response.ok) {
        throw new Error(`Error starting analysis: ${responseData.error || 'Unknown error'}`);
    }
    return responseData;
};

/**
 * Fetch peaks based on analysis id by contacting BFF
 */
export const peakCallingsData = async (analysis_id: string, apiHost: string, accessToken: string): Promise<Record<string, number>> => {
    const endpoint = `${apiHost}/analysis/${analysis_id}/peak_callings`;
    const method = 'GET';
    const response = await fetch(endpoint, {
        method: method,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
        }
    });
    const responseData = await response.json();

    if (!response.ok) {
        throw new Error(`Error loading peaks: ${responseData.error || 'Unknown error'}`);
    }
    return responseData;
};

export interface DefaultChIPAnalyses {
    analysis_id: string;
    analysis_name: string;
}

/**
 * Fetch default ChIP Analysis name/analysis id based on Experiment id by contacting BFF
 */
export const defaultChIPAnalysisbyExperiment = async (
    experiment_id: string,
    apiHost: string,
    accessToken: string
): Promise<DefaultChIPAnalyses[]> => {
    const endpoint = `${apiHost}/experiment/${experiment_id}/default_chip_analysis`;
    const method = 'GET';
    const response = await fetch(endpoint, {
        method: method,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
        }
    });

    const responseData = await response.json();

    if (!response.ok) {
        throw new Error(`Error loading default ChIP analyses : ${responseData.error || 'Unknown error'}`);
    }
    return responseData;
};

export const defaultChIPSampleGroups = async (
    experimentId: string,
    analysisId: string,
    apiHost: string,
    accessToken: string
): Promise<DefaultChIPSampleGroup[]> => {
    const response = await fetch(`${apiHost}/experiment/${experimentId}/analysis/${analysisId}/sample-groups`, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json'
        }
    });
    const responseData = await response.json();
    if (!response.ok) {
        throw new Error(`Error loading default ChIP sample groups: ${responseData.error || 'Unknown error'}`);
    }
    return responseData;
};
